// PricingPage.jsx
//
// Dedicated /pricing page. Replaces the in-line teaser on the home
// page with a full evaluation surface:
//
//   1. Hero — clear price, currency, what's included at a glance
//   2. Monthly/Annual toggle + plan cards (Basic + Pro + Enterprise)
//   3. Comparison table — every feature, ticked or X'd per plan
//   4. FAQ — the 8-10 questions every B2B SaaS buyer asks
//   5. Risk reversal — no lock-in, cancel anytime
//   6. Final CTA — Book a demo / Watch demo
//
// PRICING SOURCE OF TRUTH: `src/lib/plans.js` is the single source.
// This page imports `PLANS as PLAN_META` and `ANNUAL_DISCOUNT_PERCENT`
// + `FOUNDING_MEMBER_LOCK_MONTHS` so prices never drift between the
// marketing page, the in-app upgrade modal, the suspended-page, and
// the Paystack webhook's PLAN_CAPACITY.

import React, { useState } from 'react';
import {
  ArrowRight, Check, X, ChevronDown,
  Wallet, Clock, Database, Building2, Star, Zap,
} from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';
import { WatchDemoButton } from './WatchDemoModal';
import {
  PLANS as PLAN_META,
  ANNUAL_DISCOUNT_PERCENT,
  FOUNDING_MEMBER_LOCK_MONTHS,
} from '../../lib/plans';

// ─── Plan data ──────────────────────────────────────────────────────
// Display-layer config — feature bullets, icons, accent colors,
// CTA copy. Prices + storage caps flow from PLAN_META (lib/plans.js).
//
// Per-firm flat pricing model (v1.0.25+): each plan is one fixed price
// for the whole firm, capped at a seat limit. Firms exceeding the cap
// upgrade tier; we don't bill per-seat.

const PLAN_CARDS = [
  {
    key:       'basic',
    icon:      Star,
    tier:      'Basic',
    blurb:     'Everything a solo practice needs to leave spreadsheets behind.',
    seatLabel: 'Up to 5 lawyers',
    storage:   `${PLAN_META.basic.storageGb} GB document storage`,
    features: [
      'Unlimited case files & matters',
      'Time tracking + billing',
      'Cedi invoicing (VAT + NHIL lines)',
      'Trust accounting (3-way reconciliation)',
      'Client portal',
      'Conflict checking',
      'Email support',
    ],
    // High-intent self-serve path: the customer who knows they want
    // Basic shouldn't have to sit through a sales conversation. Both
    // Basic and Pro share the same 14-day trial (Pro features unlocked
    // during trial), so the customer picks their final tier on the
    // suspended page after the trial expires.
    cta:           'Start 14-day free trial',
    ctaTo:         'signup',
    secondaryCta:  'Or book a demo',
    secondaryTo:   'bookDemo',
    accent:        'blue',
  },
  {
    key:       'pro',
    icon:      Zap,
    tier:      'Pro',
    blurb:     'For growing firms — custom roles, advanced reports, calendar sync, and the compliance features serious firms need.',
    seatLabel: 'Up to 10 lawyers',
    storage:   `${PLAN_META.pro.storageGb} GB document storage`,
    features: [
      // Universal Pro features first — the ones every growing firm cares about
      'Everything in Basic',
      'Custom roles & permissions',
      'Custom rate cards per matter',
      'Document templates library',     // shipped v1.0.37
      'Document classification (5 tiers)',
      // Roadmap features — signaled with "Coming soon" so customers see
      // what they're buying into AND we don't oversell. Each is genuinely
      // on the roadmap and tracked for Q3 2026.
      { text: 'Google Calendar / Outlook two-way sync', soon: true },
      { text: 'Email auto-log (Gmail / Outlook)',       soon: true },
      'Advanced reports + dashboards (prior-period deltas, custom ranges)',
      // Security/compliance features last — important but specific-audience
      'End-to-end encrypted messaging',
      'Tamper-evident audit trail',
      'Bitcoin-anchored evidentiary integrity',
      'Priority support + 60-min onboarding session',
    ],
    // Same trial as Basic — customer just picks Pro on the suspended
    // page after the trial. Secondary "Or talk to sales" link for
    // customers who prefer a walkthrough before signing up.
    cta:          'Start 14-day free trial',
    ctaTo:        'signup',
    secondaryCta: 'Or talk to sales',
    secondaryTo:  'bookDemo',
    accent:       'orange',
    highlight:    true,
  },
  {
    key:       'enterprise',
    icon:      Building2,
    tier:      'Enterprise',
    blurb:     'For firms with 11+ lawyers, multi-office practices, and special compliance needs.',
    seatLabel: 'Unlimited seats',
    storage:   'Unlimited storage',
    features: [
      'Everything in Pro',
      'AI case search & document review',
      'Smart deadline alerts (AI)',
      'Dedicated account manager',
      'Custom integrations + API access',
      'SLA + dedicated support channel',
      'On-premise deployment available',
    ],
    // Enterprise is a custom contract — needs a real conversation. No
    // self-serve path for this tier; "Talk to sales" is the only CTA.
    cta:    'Talk to sales',
    ctaTo:  'bookDemo',
    accent: 'slate',
    custom: true,
  },
];

// Helpers to format the price block per (plan × cadence).
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

function priceForCard(planKey, isAnnual) {
  const meta = PLAN_META[planKey];
  if (!meta) return { primary: 'Custom', sub: 'pricing', note: null };
  if (meta.priceGhs == null) return { primary: 'Custom', sub: 'pricing', note: null };

  if (isAnnual) {
    const annual = meta.priceGhsAnnual;
    const effMonthly = Math.round(annual / 12);
    const monthly = meta.priceGhs;
    return {
      primary: `GH₵ ${fmt(effMonthly)}`,
      sub:     '/ firm / month',
      strikethrough: `GH₵ ${fmt(monthly)}`,
      note:    `Billed annually — GH₵ ${fmt(annual)} / year`,
    };
  }
  return {
    primary: `GH₵ ${fmt(meta.priceGhs)}`,
    sub:     '/ firm / month',
    note:    'Billed monthly',
  };
}

// Full feature matrix — three columns now (Basic / Pro / Enterprise).
// '✓' / '✗' / a string for "tier-specific limit" / "Pro-only with detail".
const FEATURE_MATRIX = [
  { section: 'Team & seats', features: [
    { name: 'Lawyers / users included',                      basic: 'Up to 5',          pro: 'Up to 10',           enterprise: 'Unlimited' },
    { name: 'Role-based permissions',                        basic: 'Built-in roles',   pro: 'Custom roles',       enterprise: 'Custom + SSO' },
    { name: 'Multi-tenant isolation (RLS)',                  basic: true,               pro: true,                 enterprise: true },
    { name: 'Activity audit log',                            basic: true,               pro: true,                 enterprise: true },
  ]},
  { section: 'Case management', features: [
    { name: 'Unlimited case files',                          basic: true,               pro: true,                 enterprise: true },
    { name: 'Custom statuses + practice areas',              basic: true,               pro: true,                 enterprise: true },
    { name: 'Confidential case file cascade',                basic: true,               pro: true,                 enterprise: true },
    { name: 'Court date & deadline tracking',                basic: true,               pro: true,                 enterprise: true },
    { name: 'Document classification (5 tiers)',             basic: false,              pro: true,                 enterprise: true },
    { name: 'Legal hold mode',                               basic: false,              pro: true,                 enterprise: true },
    { name: 'AI case search & document review',              basic: false,              pro: false,                enterprise: true },
    { name: 'Smart deadline alerts (AI)',                    basic: false,              pro: false,                enterprise: true },
  ]},
  { section: 'Time & billing', features: [
    { name: 'Time tracking',                                 basic: true,               pro: true,                 enterprise: true },
    { name: 'Cedi invoicing (VAT + NHIL lines)',             basic: true,               pro: true,                 enterprise: true },
    { name: 'Paystack invoice payments',                     basic: true,               pro: true,                 enterprise: true },
    { name: 'Trust accounting (3-way reconciliation)',       basic: true,               pro: true,                 enterprise: true },
    { name: 'Custom rate cards per matter',                  basic: false,              pro: true,                 enterprise: true },
  ]},
  { section: 'Productivity', features: [
    // The Productivity section. Items with 'soon' on a tier render a
    // "Coming soon" pill instead of a check — keeps the table honest
    // about what's shipped vs roadmapped.
    { name: 'Document templates library',                    basic: false,              pro: true,                 enterprise: true   },
    { name: 'Google Calendar / Outlook two-way sync',        basic: false,              pro: 'soon',               enterprise: 'soon' },
    { name: 'Email auto-log (Gmail / Outlook)',              basic: false,              pro: 'soon',               enterprise: 'soon' },
    { name: 'Advanced reports + dashboards',                 basic: false,              pro: true,                 enterprise: true   },
    { name: 'Workflow automation',                           basic: false,              pro: false,                enterprise: 'soon' },
    { name: 'API access',                                    basic: false,              pro: false,                enterprise: true   },
  ]},
  { section: 'Communication', features: [
    { name: 'Client portal',                                 basic: true,               pro: true,                 enterprise: true },
    { name: 'Team chat',                                     basic: true,               pro: true,                 enterprise: true },
    { name: 'End-to-end encrypted channels (AES-256-GCM)',   basic: false,              pro: true,                 enterprise: true },
    { name: 'Tamper-evident message hash chain',             basic: false,              pro: true,                 enterprise: true },
    { name: 'OpenTimestamps Bitcoin anchoring',              basic: false,              pro: true,                 enterprise: true },
  ]},
  { section: 'Storage & limits', features: [
    { name: 'Document storage',                              basic: `${PLAN_META.basic.storageGb} GB`,   pro: `${PLAN_META.pro.storageGb} GB`,     enterprise: 'Unlimited' },
    { name: 'Add-on storage (purchase as needed)',           basic: 'GH₵ 25 / 10 GB / mo',  pro: 'GH₵ 25 / 10 GB / mo', enterprise: 'Included' },
    { name: 'File version history',                          basic: true,               pro: true,                 enterprise: true },
  ]},
  { section: 'Support & onboarding', features: [
    { name: 'Help Center articles',                          basic: true,               pro: true,                 enterprise: true },
    { name: 'Email support',                                 basic: true,               pro: true,                 enterprise: true },
    { name: 'Priority response (under 4h business hours)',   basic: false,              pro: true,                 enterprise: true },
    { name: 'Onboarding session',                            basic: false,              pro: '60 min',             enterprise: 'Dedicated + ongoing' },
    { name: 'Dedicated account manager',                     basic: false,              pro: false,                enterprise: true },
    { name: 'Custom integrations + API',                     basic: false,              pro: false,                enterprise: true },
    { name: 'SLA + dedicated support channel',               basic: false,              pro: false,                enterprise: true },
  ]},
];

const FAQS = [
  {
    q: 'Is the price per user or per firm?',
    a: 'Per firm. Basic is GH₵ 200 / firm / month for up to 5 lawyers. Pro is GH₵ 1,000 / firm / month for up to 10 lawyers. You don\'t pay more when you add a new lawyer — you just need to upgrade tier if you cross the seat cap. This makes pricing predictable: a 5-lawyer firm and a 1-lawyer firm on Basic both pay the same.',
  },
  {
    q: 'How does the monthly vs annual billing work?',
    a: `Annual subscribers save ${ANNUAL_DISCOUNT_PERCENT}% vs paying month-by-month. Choose your cadence at signup. As a founding-member benefit, annual signups during the launch window get their rate locked for ${FOUNDING_MEMBER_LOCK_MONTHS} months — you'll keep the launch price even when we raise rates for new customers later.`,
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — 14 days of full Pro access, no credit card required. After 14 days you choose: subscribe to Basic, subscribe to Pro, contact us about Enterprise, or export your data and close the account.',
  },
  {
    q: 'Can I switch plans later?',
    a: (
      <>
        Yes. To upgrade or downgrade, email{' '}
        <a href="mailto:hello@docketflowapp.com?subject=Plan%20change%20request" className="font-semibold underline-offset-4 hover:underline" style={{ color: BRAND.orangeDark }}>
          hello@docketflowapp.com
        </a>
        {' '}and we'll handle it within one business day. You don't lose any data when you downgrade — Pro features just become unavailable until you upgrade back. We're routing changes through support during launch so we can offer the right thing for your situation (we've found firms often need a pause or a discount, not a full downgrade).
      </>
    ),
  },
  {
    q: 'What if my firm has more than 10 lawyers?',
    a: (
      <>
        That's Enterprise. We'll set up a custom contract with unlimited seats, unlimited storage, AI-powered case search + document review, a dedicated account manager, and an SLA. Pricing is volume-based —{' '}
        <button
          onClick={() => goTo('bookDemo')}
          className="font-semibold underline-offset-4 hover:underline"
          style={{ color: BRAND.orangeDark }}
        >
          book a call
        </button>
        {' '}and we'll quote you within one business day.
      </>
    ),
  },
  {
    q: 'What if I need more than 200 GB of document storage?',
    a: 'You can stack add-on storage at GH₵ 25 per 10 GB per month from inside the app (Settings → Storage → Buy more). Add-ons are flexible — buy 10 GB this month, 50 GB next month, no commitment. If you consistently need 500 GB+, Enterprise has unlimited storage included.',
  },
  {
    q: 'How does billing work? What about VAT?',
    a: 'All prices are in cedis (GHS), include VAT and NHIL, and are charged via Paystack — Ghana\'s local payment processor. You can pay with cards, MTN MoMo, AirtelTigo Cash, Vodafone Cash, or bank transfer. We never charge in USD or play FX games. Invoices are issued automatically and emailed to your firm contact.',
  },
  {
    q: 'Can I cancel anytime?',
    a: (
      <>
        Yes — no contracts, no minimum commitment, no cancellation fees. Closing your firm account is self-service from Settings → Close firm account (with 30-day data recovery window). To also cancel your Paystack subscription so we stop billing next cycle, either cancel via your Paystack dashboard OR email{' '}
        <a href="mailto:hello@docketflowapp.com?subject=Cancel%20my%20subscription" className="font-semibold underline-offset-4 hover:underline" style={{ color: BRAND.orangeDark }}>
          hello@docketflowapp.com
        </a>
        {' '}with the subject "Cancel my subscription" and we'll handle it within one business day.
      </>
    ),
  },
  {
    q: 'What happens to my data if I leave?',
    a: 'It\'s yours — always has been. Export all matters, contacts, time entries, invoices, and documents as CSV (data) and ZIP (documents) from Settings → Data export. After you close the firm account, you have 30 days to download or restore. After 30 days, the data is permanently deleted from our systems and from Supabase backups within 90 days. Already-sent invoices to your clients keep working — they have their own copies.',
  },
  {
    q: 'Is my data secure?',
    a: (
      <>
        Yes — multi-tenant isolation via Postgres row-level security, AES-256-GCM end-to-end encryption on messages (Pro), tamper-evident SHA-256 hash chain on team chat, and optional Bitcoin anchoring for evidentiary integrity. See our{' '}
        <button
          onClick={() => goTo('security')}
          className="font-semibold underline-offset-4 hover:underline"
          style={{ color: BRAND.orangeDark }}
        >
          security page
        </button>
        {' '}for the full posture.
      </>
    ),
  },
  {
    q: 'Do you offer discounts for non-profit / pro bono practices?',
    a: 'Yes — registered legal aid organizations and pro bono practices get 50% off both plans. Email hello@docketflowapp.com with proof of registration and we\'ll set it up.',
  },
  {
    q: 'Can I see the product before paying?',
    a: (
      <>
        Three options. (1) Click "Watch demo" on the home page for a 30-second animated walkthrough — no signup. (2){' '}
        <button
          onClick={() => goTo('bookDemo')}
          className="font-semibold underline-offset-4 hover:underline"
          style={{ color: BRAND.orangeDark }}
        >
          Book a demo
        </button>
        {' '}for a live 20-minute walkthrough with our team. (3) Start a 14-day trial and explore on your own.
      </>
    ),
  },
];

// ─── Page ───────────────────────────────────────────────────────────

export default function PricingPage() {
  // Monthly vs Annual toggle state — drives the price block on each
  // plan card. Default to Annual to nudge customers toward the
  // higher-LTV cadence (also surfaces the 20% savings front-and-center).
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const isAnnual = billingPeriod === 'annual';

  return (
    <main style={{ background: BRAND.bgWhite, color: BRAND.textBody }}>

      {/* ════════ HERO ════════ */}
      <section
        className="pt-16 lg:pt-24 pb-12 relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BRAND.bgWhite} 0%, ${BRAND.bgLight} 100%)` }}
      >
        <div
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] pointer-events-none"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${BRAND.blueGhost} 0%, transparent 55%)`,
            opacity: 0.6,
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
          >
            <Wallet size={12} />
            Pricing
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5"
            style={{ color: BRAND.textHead }}
          >
            Simple pricing. In cedis.
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed" style={{ color: BRAND.textMuted }}>
            One flat price per firm. Cancel anytime. No FX games, no surprise charges.
            VAT included. {' '}
            <strong style={{ color: BRAND.blueDeep }}>14-day free trial on Pro</strong> — no credit card.
          </p>
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mt-6"
            style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
          >
            <Star size={12} />
            Founding-member pricing — annual signups lock these rates for {FOUNDING_MEMBER_LOCK_MONTHS} months
          </div>
        </div>
      </section>

      {/* ════════ MONTHLY / ANNUAL TOGGLE ════════ */}
      <section className="pt-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div
            className="inline-flex items-center rounded-full p-1 text-sm"
            style={{ background: BRAND.bgLight, border: `1px solid ${BRAND.border}` }}
          >
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className="px-5 py-2 rounded-full transition"
              style={{
                background:  isAnnual ? 'transparent' : BRAND.bgWhite,
                color:       isAnnual ? BRAND.textMuted : BRAND.textHead,
                fontWeight:  isAnnual ? 500 : 600,
                boxShadow:   isAnnual ? 'none' : '0 1px 2px rgba(15,42,95,0.08)',
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annual')}
              className="px-5 py-2 rounded-full transition flex items-center gap-2"
              style={{
                background:  isAnnual ? BRAND.bgWhite : 'transparent',
                color:       isAnnual ? BRAND.textHead : BRAND.textMuted,
                fontWeight:  isAnnual ? 600 : 500,
                boxShadow:   isAnnual ? '0 1px 2px rgba(15,42,95,0.08)' : 'none',
              }}
            >
              Annual
              <span
                className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: '#10b98122', color: '#047857' }}
              >
                Save {ANNUAL_DISCOUNT_PERCENT}%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ════════ PLAN CARDS ════════ */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {PLAN_CARDS.map((p) => (
              <PricingCard key={p.key} plan={p} isAnnual={isAnnual} />
            ))}
          </div>
          <p className="text-center text-sm mt-8" style={{ color: BRAND.textMuted }}>
            Have 11+ lawyers, or specific compliance / integration needs?{' '}
            <button
              onClick={() => goTo('bookDemo')}
              className="font-semibold underline-offset-4 hover:underline"
              style={{ color: BRAND.orangeDark }}
            >
              Talk to us about Enterprise →
            </button>
          </p>
        </div>
      </section>

      {/* ════════ COMPARISON TABLE ════════ */}
      <section className="py-16" style={{ background: BRAND.bgLight }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.orangeDark }}>
              Compare every feature
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: BRAND.textHead }}>
              What's in each plan.
            </h2>
          </div>

          <div
            className="rounded-2xl overflow-hidden border bg-white"
            style={{ borderColor: BRAND.border, boxShadow: '0 16px 48px rgba(15,42,95,0.06)' }}
          >
            <FeatureMatrix />
          </div>
        </div>
      </section>

      {/* ════════ RISK REVERSAL ════════ */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: '14-day free trial',
                sub:   'Full Pro access. No credit card. Cancel before day 14 and pay nothing.',
              },
              {
                icon: Wallet,
                title: 'No lock-in',
                sub:   'No contracts, no minimum commitment. Cancel anytime from Settings. Pro-rated refunds on annual plans.',
              },
              {
                icon: Database,
                title: 'Your data, exportable',
                sub:   'Download every matter, contact, time entry, invoice, and document at any time. We don\'t hold your data hostage.',
              },
            ].map((item) => {
              const Ic = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl p-6 border"
                  style={{ background: BRAND.bgWhite, borderColor: BRAND.border }}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
                  >
                    <Ic size={18} />
                  </div>
                  <p className="text-base font-bold mb-2" style={{ color: BRAND.textHead }}>
                    {item.title}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
                    {item.sub}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════ FAQ ════════ */}
      <section className="py-16" style={{ background: BRAND.bgLight }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.orangeDark }}>
              Frequently asked
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: BRAND.textHead }}>
              Questions, answered.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => <Faq key={i} q={faq.q} a={faq.a} defaultOpen={i === 0} />)}
          </div>
        </div>
      </section>

      {/* ════════ FINAL CTA ════════ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl p-10 lg:p-14 text-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.blueDeep} 0%, ${BRAND.blueMid} 100%)`,
              color: '#fff',
              boxShadow: '0 24px 64px rgba(15,42,95,0.30)',
            }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
              Still deciding?
            </h2>
            <p
              className="text-base sm:text-lg max-w-xl mx-auto mb-8 leading-relaxed"
              style={{ color: 'rgba(226,232,240,0.85)' }}
            >
              Watch a 30-second demo, or book a live 20-minute walkthrough with our team.
              Either way, no commitment.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => goTo('bookDemo')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-xl w-full sm:w-auto"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(249,115,22,0.40)',
                }}
              >
                Book a demo
                <ArrowRight size={16} />
              </button>
              <WatchDemoButton variant="ghost" size="lg" label="Watch demo (30s)" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// ─── Pricing card ──────────────────────────────────────────────────

function PricingCard({ plan, isAnnual }) {
  const isOrange = plan.accent === 'orange';
  const Icon     = plan.icon;
  const price    = priceForCard(plan.key, isAnnual);

  return (
    <div
      className="relative rounded-2xl p-7 lg:p-8 border-2 transition-transform flex flex-col"
      style={{
        background: BRAND.bgWhite,
        borderColor: plan.highlight ? BRAND.orange : BRAND.border,
        boxShadow: plan.highlight
          ? '0 24px 64px rgba(249,115,22,0.18), 0 8px 24px rgba(15,42,95,0.08)'
          : '0 8px 32px rgba(15,42,95,0.06)',
      }}
    >
      {plan.highlight && (
        <div
          className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
            color: '#fff',
          }}
        >
          Most popular
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              background: isOrange ? BRAND.orangeSoft : (plan.custom ? '#f1f5f9' : BRAND.blueGhost),
              color:      isOrange ? BRAND.orangeDark : (plan.custom ? '#475569'  : BRAND.blueDeep),
            }}
          >
            <Icon size={18} />
          </div>
        )}
        <p className="text-base font-bold" style={{ color: BRAND.textHead }}>
          {plan.tier}
        </p>
      </div>

      {/* Price block — strikethrough monthly when annual is selected */}
      <div className="mb-3">
        {price.strikethrough && (
          <div className="text-xs line-through leading-none mb-1" style={{ color: BRAND.textFaint }}>
            {price.strikethrough}
          </div>
        )}
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold" style={{ color: BRAND.textHead }}>
            {price.primary}
          </span>
          <span className="text-sm" style={{ color: BRAND.textMuted }}>
            {price.sub}
          </span>
        </div>
        {price.note && (
          <div className="text-[11px] mt-1" style={{ color: BRAND.textMuted }}>
            {price.note}
          </div>
        )}
      </div>

      {/* Seats + storage badges — first-class, not buried in the bullet list */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span
          className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: BRAND.bgLight, color: BRAND.textHead }}
        >
          {plan.seatLabel}
        </span>
        <span
          className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
        >
          {plan.storage}
        </span>
      </div>

      <p className="text-sm mb-5 leading-relaxed" style={{ color: BRAND.textMuted }}>
        {plan.blurb}
      </p>

      <ul className="space-y-2.5 mb-7 flex-grow">
        {plan.features.map((f) => {
          // Feature items can be either a plain string OR
          // { text: string, soon: boolean } — the latter renders a
          // "Coming soon" pill so customers see the roadmap without
          // us overselling current functionality.
          const text = typeof f === 'string' ? f : f.text;
          const soon = typeof f === 'object' && f.soon;
          return (
            <li key={text} className="flex items-start gap-2.5 text-sm" style={{ color: soon ? BRAND.textMuted : BRAND.textBody }}>
              <Check size={16} className="mt-0.5 shrink-0" style={{ color: soon ? BRAND.textFaint : (isOrange ? BRAND.orangeDark : (plan.custom ? '#475569' : BRAND.blueDeep)) }} />
              <span className="flex-1">
                {text}
                {soon && (
                  <span
                    className="ml-2 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-baseline"
                    style={{ background: '#fef3c7', color: '#92400e' }}
                  >
                    Coming soon
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="space-y-3">
        <button
          onClick={() => goTo(plan.ctaTo)}
          className="w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-xl transition-all"
          style={
            plan.highlight
              ? {
                  background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                  color: '#fff',
                  boxShadow: '0 6px 20px rgba(249,115,22,0.30)',
                }
              : plan.custom
                ? {
                    background: 'transparent',
                    color: BRAND.blueDeep,
                    border: `1.5px solid ${BRAND.border}`,
                  }
                : {
                    background: BRAND.blueDeep,
                    color: '#fff',
                  }
          }
        >
          {plan.cta}
          <ArrowRight size={14} />
        </button>

        {/* Secondary CTA — appears below the primary for tiers that have
            both a self-serve and a sales path (Basic + Pro). Enterprise
            doesn't define one (sales conversation is the only path). */}
        {plan.secondaryCta && (
          <button
            onClick={() => goTo(plan.secondaryTo)}
            className="w-full text-center text-xs font-medium underline-offset-4 hover:underline transition"
            style={{ color: BRAND.textMuted }}
          >
            {plan.secondaryCta} →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Feature matrix table ──────────────────────────────────────────

function FeatureMatrix() {
  return (
    <table className="w-full text-sm">
      <thead style={{ background: BRAND.bgLight }}>
        <tr>
          <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.textMuted }}>
            Feature
          </th>
          <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.blueDeep }}>
            Basic
          </th>
          <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.orangeDark }}>
            Pro
          </th>
          <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#475569' }}>
            Enterprise
          </th>
        </tr>
      </thead>
      <tbody>
        {FEATURE_MATRIX.map((section) => (
          <React.Fragment key={section.section}>
            <tr style={{ background: BRAND.bgLighter }}>
              <td colSpan={4} className="px-5 py-2 text-xs font-bold uppercase tracking-widest" style={{ color: BRAND.textHead }}>
                {section.section}
              </td>
            </tr>
            {section.features.map((f) => (
              <tr key={f.name} className="border-t" style={{ borderColor: BRAND.borderSoft }}>
                <td className="px-5 py-3 text-sm" style={{ color: BRAND.textBody }}>
                  {f.name}
                </td>
                <td className="px-5 py-3 text-center">
                  <FeatureValue value={f.basic} accent="blue" />
                </td>
                <td className="px-5 py-3 text-center">
                  <FeatureValue value={f.pro} accent="orange" />
                </td>
                <td className="px-5 py-3 text-center">
                  <FeatureValue value={f.enterprise} accent="slate" />
                </td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

function FeatureValue({ value, accent }) {
  const checkColor = accent === 'orange' ? BRAND.orangeDark
                   : accent === 'slate'  ? '#475569'
                   :                       BRAND.blueDeep;
  if (value === true) {
    return <Check size={16} className="mx-auto" style={{ color: checkColor }} />;
  }
  if (value === false) {
    return <X size={14} className="mx-auto" style={{ color: BRAND.textFaint }} />;
  }
  // 'soon' — roadmap feature, render an amber "Coming soon" pill.
  if (value === 'soon') {
    return (
      <span
        className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
        style={{ background: '#fef3c7', color: '#92400e' }}
      >
        Coming soon
      </span>
    );
  }
  // Other string — tier-specific limit or Pro/Enterprise-only detail.
  return (
    <span className="text-xs font-semibold" style={{ color: BRAND.textHead }}>
      {value}
    </span>
  );
}

// ─── FAQ accordion ─────────────────────────────────────────────────

function Faq({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div
      className="rounded-xl border overflow-hidden transition-all"
      style={{
        background: BRAND.bgWhite,
        borderColor: open ? BRAND.blueSoft : BRAND.border,
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold pr-4" style={{ color: BRAND.textHead }}>
          {q}
        </span>
        <ChevronDown
          size={18}
          className="shrink-0 transition-transform"
          style={{
            color: BRAND.textMuted,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm leading-relaxed" style={{ color: BRAND.textBody }}>
          {a}
        </div>
      )}
    </div>
  );
}
